/**
 * BatchPuzzles — batch puzzle compositor modal.
 * Replaces window.BatchPuzzles.
 */
import { useState, useEffect, useRef, useCallback, type FC } from 'react';
import { Icon } from './Icon';
import { apiPost, apiPostForm } from '../services/api';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const A4_WIDTH = 2480;
const A4_HEIGHT = 3508;
const INITIAL_TRANSFORM = { x: A4_WIDTH / 2, y: A4_HEIGHT / 2, scale: 1, rotation: 0 };

export const BatchPuzzles: FC<Props> = ({ isOpen, onClose }) => {
  const [frameImage, setFrameImage] = useState<string | null>(null);
  const [sampleImage, setSampleImage] = useState<string | null>(null);
  const [renderRotation, setRenderRotation] = useState(0);
  const [transform, setTransform] = useState({ ...INITIAL_TRANSFORM });
  const [configLoaded, setConfigLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, text: '' });
  const [isDragOver, setIsDragOver] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const transformRef = useRef(transform);
  const isDraggingRef = useRef(false);
  const lastMouseRef = useRef({ x: 0, y: 0 });
  const frameImgRef = useRef<HTMLImageElement | null>(null);
  const sampleImgRef = useRef<HTMLImageElement | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadImg = (src: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

  const readFile = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.onerror = reject;
      r.readAsDataURL(file);
    });

  const loadConfigFromServer = async () => {
    try {
      const data = await (await fetch('/api/batch-puzzles/config')).json();
      if (data.success && data.config) {
        const c = data.config;
        if (c.transform) setTransform(c.transform);
        if (c.renderRotation != null) setRenderRotation(c.renderRotation);
        if (c.frameUrl) setFrameImage(c.frameUrl + '?t=' + Date.now());
        if (c.sampleUrl) setSampleImage(c.sampleUrl + '?t=' + Date.now());
      }
    } catch (e) {
      console.error('Failed to load batch puzzles config:', e);
    } finally {
      setConfigLoaded(true);
    }
  };

  const saveConfigToServer = useCallback(
    async (t?: typeof transform, rot?: number) => {
      setSaving(true);
      try {
        await apiPost('/api/batch-puzzles/config', {
          config: {
            transform: t || transform,
            renderRotation: rot != null ? rot : renderRotation,
            frameUrl:
              typeof frameImage === 'string' && !frameImage.startsWith('data:')
                ? frameImage.split('?')[0]
                : null,
            sampleUrl:
              typeof sampleImage === 'string' && !sampleImage.startsWith('data:')
                ? sampleImage.split('?')[0]
                : null,
          },
        });
      } catch (e) {
        console.error(e);
      } finally {
        setSaving(false);
      }
    },
    [transform, renderRotation, frameImage, sampleImage],
  );

  const scheduleSave = useCallback(
    (t: typeof transform, rot: number) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => saveConfigToServer(t, rot), 800);
    },
    [saveConfigToServer],
  );

  useEffect(() => {
    if (isOpen && !configLoaded) loadConfigFromServer();
  }, [isOpen, configLoaded]);
  useEffect(() => {
    transformRef.current = transform;
  }, [transform]);

  useEffect(() => {
    if (frameImage)
      loadImg(frameImage).then((img) => {
        frameImgRef.current = img;
      });
    else frameImgRef.current = null;
  }, [frameImage]);
  useEffect(() => {
    if (sampleImage)
      loadImg(sampleImage).then((img) => {
        sampleImgRef.current = img;
      });
    else sampleImgRef.current = null;
  }, [sampleImage]);

  const drawComposite = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      bgImg: HTMLImageElement | null,
      fImg: HTMLImageElement | null,
      t: typeof transform,
    ) => {
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      if (bgImg) {
        ctx.save();
        ctx.translate(t.x, t.y);
        ctx.rotate((t.rotation * Math.PI) / 180);
        ctx.scale(t.scale, t.scale);
        ctx.drawImage(bgImg, -bgImg.width / 2, -bgImg.height / 2);
        ctx.restore();
      } else {
        ctx.fillStyle = '#1e293b';
        ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      }
      if (fImg) ctx.drawImage(fImg, 0, 0, ctx.canvas.width, ctx.canvas.height);
    },
    [],
  );

  const applyRenderRotation = useCallback(async (sourceCanvas: HTMLCanvasElement, rot: number) => {
    // Helper: convert canvas to JPEG blob with white background
    const toJpegBlob = (cvs: HTMLCanvasElement) => {
      const tmp = document.createElement('canvas');
      tmp.width = cvs.width;
      tmp.height = cvs.height;
      const tCtx = tmp.getContext('2d')!;
      tCtx.fillStyle = '#ffffff';
      tCtx.fillRect(0, 0, tmp.width, tmp.height);
      tCtx.drawImage(cvs, 0, 0);
      return new Promise<Blob | null>((r) => tmp.toBlob(r, 'image/jpeg', 0.92));
    };
    if (!rot || (rot !== 90 && rot !== -90)) return toJpegBlob(sourceCanvas);
    const w = sourceCanvas.width;
    const h = sourceCanvas.height;
    const rc = document.createElement('canvas');
    rc.width = h;
    rc.height = w;
    const rCtx = rc.getContext('2d')!;
    rCtx.translate(rc.width / 2, rc.height / 2);
    rCtx.rotate((rot * Math.PI) / 180);
    rCtx.drawImage(sourceCanvas, -w / 2, -h / 2);
    return toJpegBlob(rc);
  }, []);

  // Render loop
  useEffect(() => {
    if (!isOpen) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let rafId: number;
    const render = () => {
      drawComposite(ctx, sampleImgRef.current, frameImgRef.current, transformRef.current);
      rafId = requestAnimationFrame(render);
    };
    render();
    return () => cancelAnimationFrame(rafId);
  }, [isOpen, frameImage, sampleImage, drawComposite]);

  // Upload handlers
  const uploadAsset = async (file: File, assetType: string) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('assetType', assetType);
    const res = await apiPostForm<{ success: boolean; url: string }>(
      '/api/batch-puzzles/upload-asset',
      fd,
    );
    if (res.success) return res.url;
    throw new Error('Upload failed');
  };

  const handleFrameUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFrameImage(await readFile(file));
    try {
      const url = await uploadAsset(file, 'frame');
      setFrameImage(url + '?t=' + Date.now());
      setTimeout(() => saveConfigToServer(), 300);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSampleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSampleImage(await readFile(file));
    try {
      const url = await uploadAsset(file, 'sample');
      setSampleImage(url + '?t=' + Date.now());
      setTimeout(() => saveConfigToServer(), 300);
    } catch (err) {
      console.error(err);
    }
  };

  // Canvas drag
  const handleMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    if (!sampleImage) return;
    isDraggingRef.current = true;
    const cx = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const cy = 'touches' in e ? e.touches[0].clientY : e.clientY;
    lastMouseRef.current = { x: cx, y: cy };
  };
  const handleMouseMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDraggingRef.current || !sampleImage) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const cx = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const cy = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const bounds = canvas.getBoundingClientRect();
    const dx = (cx - lastMouseRef.current.x) * (A4_WIDTH / bounds.width);
    const dy = (cy - lastMouseRef.current.y) * (A4_HEIGHT / bounds.height);
    transformRef.current = {
      ...transformRef.current,
      x: transformRef.current.x + dx,
      y: transformRef.current.y + dy,
    };
    lastMouseRef.current = { x: cx, y: cy };
  };
  const handleMouseUp = () => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      const newT = { ...transformRef.current };
      setTransform(newT);
      scheduleSave(newT, renderRotation);
    }
  };

  // File handlers
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setFiles((prev) => [
      ...prev,
      ...Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/')),
    ]);
  }, []);
  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files)
      setFiles((prev) => [
        ...prev,
        ...Array.from(e.target.files!).filter((f) => f.type.startsWith('image/')),
      ]);
  }, []);
  const removeFile = (idx: number) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const cycleRotation = () =>
    setRenderRotation((prev) => {
      const next = prev === 0 ? 90 : prev === 90 ? -90 : 0;
      scheduleSave(transform, next);
      return next;
    });

  // Process batch
  const handleProcess = async () => {
    if (!frameImage || files.length === 0) return;
    setProcessing(true);
    setProgress({ current: 0, total: files.length, text: 'Инициализация...' });
    try {
      const fImg = await loadImg(frameImage);
      const canvas = document.createElement('canvas');
      canvas.width = A4_WIDTH;
      canvas.height = A4_HEIGHT;
      const ctx = canvas.getContext('2d')!;

      if (files.length === 1) {
        setProgress({ current: 1, total: 1, text: 'Обработка...' });
        const contentImg = await loadImg(await readFile(files[0]));
        drawComposite(ctx, contentImg, fImg, transform);
        const blob = await applyRenderRotation(canvas, renderRotation);
        if (blob) saveAs(blob, files[0].name.replace(/\.[^/.]+$/, '') + '_print.jpg');
      } else {
        const zip = new JSZip();
        for (let i = 0; i < files.length; i++) {
          setProgress({
            current: i + 1,
            total: files.length,
            text: `Обработка ${files[i].name}...`,
          });
          try {
            const contentImg = await loadImg(await readFile(files[i]));
            drawComposite(ctx, contentImg, fImg, transform);
            const blob = await applyRenderRotation(canvas, renderRotation);
            if (blob) zip.file(files[i].name.replace(/\.[^/.]+$/, '') + '_print.jpg', blob);
          } catch (err) {
            console.error('Error processing', files[i].name, err);
          }
        }
        setProgress({ current: files.length, total: files.length, text: 'Упаковка в ZIP...' });
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        saveAs(zipBlob, 'batch_puzzles.zip');
      }
      setProgress((prev) => ({ ...prev, text: 'Готово! Скачивание началось.' }));
    } catch (err) {
      console.error(err);
      setProgress((prev) => ({ ...prev, text: 'Ошибка при обработке.' }));
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setProcessing(false);
    setProgress({ current: 0, total: 0, text: '' });
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !processing) handleClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [isOpen, processing]);

  if (!isOpen) return null;
  const pct = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-slate-950/95 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget && !processing) handleClose();
      }}
    >
      <div
        className="flex-1 flex flex-col bg-slate-900 m-3 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700 bg-slate-800/80 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-blue-600 to-indigo-600 w-8 h-8 rounded-lg flex items-center justify-center shadow-lg">
              <Icon name="layout-template" className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-base font-bold text-white">Batch Puzzles</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={processing}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors disabled:opacity-50"
          >
            <Icon name="x" className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="flex flex-col lg:flex-row gap-4 p-4 pb-0">
            {/* Controls */}
            <div className="w-full lg:w-72 flex flex-col gap-3 flex-shrink-0">
              {/* Frame Upload */}
              <div className="p-3 bg-slate-800 rounded-xl border border-slate-700">
                <h3 className="text-xs font-semibold text-white mb-2 flex items-center gap-2">
                  <Icon name="image" className="w-3.5 h-3.5 text-blue-400" />
                  Фрейм (передний план)
                </h3>
                <div className="relative cursor-pointer border-2 border-dashed border-slate-600 rounded-lg p-3 hover:border-blue-500 hover:bg-blue-900/10 transition-colors text-center">
                  <input
                    type="file"
                    accept="image/png"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    onChange={handleFrameUpload}
                  />
                  {frameImage ? (
                    <div className="flex items-center justify-center gap-2 text-green-400">
                      <Icon name="check-circle" className="w-5 h-5" />
                      <span className="text-sm font-medium">Загружен</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2 text-slate-400">
                      <Icon name="upload" className="w-5 h-5" />
                      <span className="text-sm">PNG с прозрачностью</span>
                    </div>
                  )}
                </div>
              </div>
              {/* Sample Upload */}
              <div className="p-3 bg-slate-800 rounded-xl border border-slate-700">
                <h3 className="text-xs font-semibold text-white mb-2 flex items-center gap-2">
                  <Icon name="image" className="w-3.5 h-3.5 text-blue-400" />
                  Сэмпл (задний фон)
                </h3>
                <div className="relative cursor-pointer border-2 border-dashed border-slate-600 rounded-lg p-3 hover:border-blue-500 hover:bg-blue-900/10 transition-colors text-center">
                  <input
                    type="file"
                    accept="image/*"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    onChange={handleSampleUpload}
                  />
                  {sampleImage ? (
                    <div className="flex items-center justify-center gap-2 text-green-400">
                      <Icon name="check-circle" className="w-5 h-5" />
                      <span className="text-sm font-medium">Загружен</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2 text-slate-400">
                      <Icon name="upload" className="w-5 h-5" />
                      <span className="text-sm">Для настройки позиции</span>
                    </div>
                  )}
                </div>
              </div>
              {/* Transform */}
              <div className="p-3 bg-slate-800 rounded-xl border border-slate-700">
                <h3 className="text-xs font-semibold text-white mb-3 flex items-center gap-2">
                  <Icon name="move" className="w-3.5 h-3.5 text-blue-400" />
                  Настройка позиции
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="flex items-center justify-between text-xs font-medium text-slate-400 mb-1">
                      <span className="flex items-center gap-1">
                        <Icon name="zoom-in" className="w-3 h-3" />
                        Масштаб
                      </span>
                      <span className="text-white">{transform.scale.toFixed(2)}x</span>
                    </label>
                    <input
                      type="range"
                      min="0.1"
                      max="3.0"
                      step="0.01"
                      value={transform.scale}
                      onChange={(e) => {
                        const newT = { ...transform, scale: parseFloat(e.target.value) };
                        setTransform(newT);
                        scheduleSave(newT, renderRotation);
                      }}
                      className="w-full h-1.5 bg-slate-900 rounded-lg appearance-none cursor-pointer accent-blue-500"
                    />
                  </div>
                  <div>
                    <label className="flex items-center justify-between text-xs font-medium text-slate-400 mb-1">
                      <span className="flex items-center gap-1">
                        <Icon name="rotate-cw" className="w-3 h-3" />
                        Поворот
                      </span>
                      <span className="text-white">{transform.rotation}°</span>
                    </label>
                    <input
                      type="range"
                      min="-180"
                      max="180"
                      step="1"
                      value={transform.rotation}
                      onChange={(e) => {
                        const newT = { ...transform, rotation: parseInt(e.target.value) };
                        setTransform(newT);
                        scheduleSave(newT, renderRotation);
                      }}
                      className="w-full h-1.5 bg-slate-900 rounded-lg appearance-none cursor-pointer accent-blue-500"
                    />
                  </div>
                </div>
              </div>
              {/* Render Rotation */}
              <div className="p-3 bg-slate-800 rounded-xl border border-slate-700">
                <h3 className="text-xs font-semibold text-white mb-2 flex items-center gap-2">
                  <Icon name="rotate-cw" className="w-3.5 h-3.5 text-blue-400" />
                  Поворот после рендера
                </h3>
                <button
                  onClick={cycleRotation}
                  className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg border text-sm font-medium transition-colors ${renderRotation ? 'bg-indigo-500/20 border-indigo-500 text-indigo-300' : 'bg-slate-900 border-slate-700 text-slate-400 hover:text-white hover:border-slate-600'}`}
                >
                  <Icon name="rotate-cw" className="w-4 h-4" />
                  {renderRotation === 90 ? '+90°' : renderRotation === -90 ? '-90°' : '0° (выкл)'}
                </button>
              </div>
              <p className="text-[11px] text-slate-500 px-1">
                Перетаскивайте изображение на холсте для настройки положения.
              </p>
            </div>

            {/* Canvas preview */}
            <div
              className="flex-1 bg-slate-900/50 rounded-xl border border-slate-700 flex items-center justify-center p-3 overflow-hidden relative select-none min-h-[250px] lg:min-h-0"
              style={{ touchAction: 'none' }}
            >
              <div
                className="relative shadow-2xl bg-white ring-1 ring-slate-900/10"
                style={{
                  aspectRatio: `${A4_WIDTH}/${A4_HEIGHT}`,
                  maxHeight: '100%',
                  maxWidth: '100%',
                }}
              >
                <canvas
                  ref={canvasRef}
                  width={A4_WIDTH}
                  height={A4_HEIGHT}
                  className="w-full h-full object-contain cursor-move block"
                  style={{ touchAction: 'none' }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  onTouchStart={handleMouseDown}
                  onTouchMove={handleMouseMove}
                  onTouchEnd={handleMouseUp}
                />
                {!frameImage && !sampleImage && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-slate-900/50">
                    <p className="text-white/40 text-base font-bold uppercase tracking-widest">
                      A4 Превью
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bottom: Batch */}
          <div className="p-4 pt-4">
            <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
              {/* Drop Area */}
              <div
                onDrop={handleDrop}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragOver(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setIsDragOver(false);
                }}
                className={`p-5 border-b border-slate-700 text-center transition-colors cursor-pointer relative ${isDragOver ? 'bg-blue-900/20 border-blue-500' : 'bg-slate-900/30 hover:bg-slate-900/50'} ${processing ? 'pointer-events-none opacity-50' : ''}`}
              >
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  onChange={handleFileInput}
                  disabled={processing}
                />
                <div className="flex flex-col items-center justify-center space-y-1">
                  <div className="p-2 bg-slate-800 rounded-full border border-slate-700">
                    <Icon name="archive" className="w-5 h-5 text-blue-400" />
                  </div>
                  <p className="text-sm font-medium text-white">
                    {isDragOver ? 'Отпустите файлы...' : 'Перетащите картинки сюда'}
                  </p>
                  <p className="text-xs text-slate-500">или нажмите для выбора</p>
                </div>
              </div>

              {files.length > 0 && (
                <div className="p-3 max-h-44 overflow-y-auto">
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-2">
                    {files.map((file, idx) => (
                      <div
                        key={`${file.name}-${idx}`}
                        className="group relative bg-slate-900 p-2 rounded-lg border border-slate-700 flex flex-col items-center"
                      >
                        {!processing && (
                          <button
                            onClick={() => removeFile(idx)}
                            className="absolute top-1 right-1 text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity text-sm"
                          >
                            ×
                          </button>
                        )}
                        <div className="w-7 h-7 bg-slate-800 rounded flex items-center justify-center mb-1 border border-slate-700">
                          <Icon name="file-image" className="w-3.5 h-3.5 text-blue-400" />
                        </div>
                        <span
                          className="text-[10px] text-center text-slate-300 truncate w-full"
                          title={file.name}
                        >
                          {file.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="px-4 py-3 bg-slate-800 border-t border-slate-700 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  {processing && (
                    <div className="flex items-center gap-3 w-full sm:w-64">
                      <Icon
                        name="loader-2"
                        className="w-5 h-5 text-blue-400 animate-spin flex-shrink-0"
                      />
                      <div className="flex-1">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="font-medium text-white">{progress.text}</span>
                          <span className="text-slate-400">{pct}%</span>
                        </div>
                        <div className="w-full bg-slate-900 rounded-full h-1.5 overflow-hidden">
                          <div
                            className="bg-blue-600 h-full transition-all duration-300"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {!processing && files.length > 0 && (
                    <span className="text-xs text-slate-400">
                      {files.length} файл(ов)
                      {renderRotation
                        ? ` · поворот ${renderRotation > 0 ? '+' : ''}${renderRotation}°`
                        : ''}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <button
                    onClick={() => setFiles([])}
                    disabled={!files.length || processing}
                    className="flex-1 sm:flex-none px-4 py-2 rounded-lg text-xs font-medium border border-slate-600 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors disabled:opacity-40"
                  >
                    Очистить
                  </button>
                  <button
                    onClick={handleProcess}
                    disabled={!files.length || !frameImage || processing}
                    className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2 rounded-lg text-xs font-medium transition-all ${files.length && frameImage && !processing ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg' : 'bg-slate-700 text-slate-500 cursor-not-allowed'}`}
                  >
                    <Icon name="download" className="w-3.5 h-3.5" />
                    {processing ? 'Обработка...' : 'Экспорт'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
